import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Accordion, Box, Button, Grid, Group, Loader, Progress, Stack, Text } from "@mantine/core";
import { IconCheck, IconClock } from "@tabler/icons-react";
import { deleteRunPodJob, getRunPodStatus, isTerminalStatus, retryRunPodJob, type RunPodJob } from "../../api/runpod";
import { formatDuration } from "../../lib/format";
import { progressLabel } from "../../lib/progressLabels";
import { useAsyncAction } from "../../lib/useAsyncAction";
import GlbViewer from "../../components/viewer/GlbViewer";
import { ConfirmButton, DownloadButton, ErrorState, ImageZoom, LoadingState, Page, StatusBadge } from "../../components/ui";

const POLL_INTERVAL_IN_QUEUE_MS = 6000;
const POLL_INTERVAL_IN_PROGRESS_MS = 4500;
const POLL_INTERVAL_HIDDEN_TAB_MS = 15000;

function getPollIntervalMs(status: RunPodJob["status"], isDocumentHidden: boolean) {
  if (isDocumentHidden) return POLL_INTERVAL_HIDDEN_TAB_MS;
  if (status === "IN_QUEUE") return POLL_INTERVAL_IN_QUEUE_MS;
  return POLL_INTERVAL_IN_PROGRESS_MS;
}

export default function JobView({ jobId }: { jobId: string }) {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [job, setJob] = useState<RunPodJob | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isDocumentHidden, setIsDocumentHidden] = useState(() =>
    typeof document !== "undefined" ? document.hidden : false,
  );

  const startTimeRef = useRef(0);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTerminal = (s?: RunPodJob["status"]) => s !== undefined && isTerminalStatus(s);

  useEffect(() => {
    if (!job) return;
    const createdAtMs = Date.parse(job.createdAt);
    startTimeRef.current = Number.isFinite(createdAtMs) ? createdAtMs : Date.now();
    if (!isTerminal(job.status)) {
      setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
    }
  }, [job]);

  useEffect(() => {
    if (!job || isTerminal(job.status)) {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      return;
    }
    elapsedIntervalRef.current = setInterval(() => setElapsedMs(Date.now() - startTimeRef.current), 200);
    return () => {
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, [job]);

  useEffect(() => {
    if (isTerminal(job?.status)) return;
    const delay = job ? getPollIntervalMs(job.status, isDocumentHidden) : 0;
    pollTimeoutRef.current = setTimeout(async () => {
      try {
        const updated = await getRunPodStatus(instance, jobId);
        setJob(updated);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : "Status check failed");
      }
    }, delay);
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [instance, jobId, job, isDocumentHidden]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleVisibilityChange = () => setIsDocumentHidden(document.hidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/gallery/serverless"));

  // Retry reuses the same DB row (same internal id / URL); only the RunPod provider id changes,
  // so the current route stays valid and no re-navigation is needed.
  const retry = useAsyncAction(async () => {
    setJob(await retryRunPodJob(instance, jobId));
    setFetchError(null);
  });

  return (
    <Page
      title="Serverless"
      onBack={goBack}
      maw={640}
      actions={
        <ConfirmButton
          message="Delete this job and its record? The input and output blobs stay in storage."
          onConfirm={async () => {
            await deleteRunPodJob(instance, jobId);
            navigate("/gallery/serverless");
          }}
        />
      }
    >
      <Group gap="xs">
        <Text size="sm" c="dimmed">
          Job ID
        </Text>
        <Text size="sm" ff="monospace">
          {job === null ? <Loader size="xs" /> : job.runPodJobId}
        </Text>
      </Group>

      {fetchError && <ErrorState message={fetchError} />}
      {retry.error && <ErrorState message={retry.error} title="Retry failed" />}

      {job ? (
        <Accordion multiple defaultValue={["original", "status"]} variant="separated">
          {job.inputBlobUrl && (
            <Accordion.Item value="original">
              <Accordion.Control>
                <Text fw={600}>Original Image</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Grid>
                  <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
                    <Stack gap="sm">
                      <ImageZoom src={job.inputBlobUrl} alt="original" withBorder={false} />
                      <DownloadButton href={job.inputBlobUrl} fullWidth />
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          <Accordion.Item value="status">
            <Accordion.Control>
              <Text fw={600}>Status</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <StatusCard job={job} elapsedMs={elapsedMs} retrying={retry.busy} onRetry={() => void retry.run()} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : !fetchError ? (
        <LoadingState h={80} />
      ) : null}

      {job?.status === "COMPLETED" && job.outputBlobUrl && (
        <Stack gap="sm">
          <Group gap="xs">
            <IconCheck size={16} color="var(--mantine-color-teal-6)" />
            <Text size="sm" fw={500} c="teal">
              Output ready
            </Text>
          </Group>

          {job.outputFileName && (
            <Group gap="xs" wrap="wrap">
              <Text size="xs" c="dimmed">
                {job.outputFileName}
              </Text>
              {job.outputSizeBytes && (
                <>
                  <Text size="xs" c="dimmed">
                    ·
                  </Text>
                  <Text size="xs" c="dimmed">
                    {(job.outputSizeBytes / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </>
              )}
            </Group>
          )}

          <DownloadButton href={job.outputBlobUrl} label="Download GLB" size="sm" style={{ alignSelf: "flex-start" }} />

          <GlbViewer key={job.outputBlobUrl} url={job.outputBlobUrl} />
        </Stack>
      )}

    </Page>
  );
}

function StatusCard({
  job,
  elapsedMs,
  retrying,
  onRetry,
}: {
  job: RunPodJob;
  elapsedMs: number;
  retrying: boolean;
  onRetry: () => void;
}) {
  const isActive = job.status === "IN_QUEUE" || job.status === "IN_PROGRESS";

  return (
    <Box
      p="md"
      style={{
        borderRadius: 8,
        border: "1px solid var(--mantine-color-default-border)",
        background: "var(--mantine-color-default)",
      }}
    >
      <Stack gap="xs">
        <Group gap="sm" justify="space-between" align="center">
          <Group gap="sm">
            {isActive && <Loader size="xs" />}
            <StatusBadge status={job.status} />
            {job.workerId && (
              <Text size="xs" c="dimmed" ff="monospace">
                worker: {job.workerId}
              </Text>
            )}
          </Group>

          {job.status === "FAILED" && (
            <Button size="xs" variant="light" color="red" loading={retrying} onClick={onRetry}>
              Retry
            </Button>
          )}
        </Group>

        {isActive && job.progressStep !== null && <JobProgress job={job} />}

        <Group gap="lg">
          {job.delayTimeMs !== null && job.delayTimeMs !== undefined && (
            <Group gap={4}>
              <IconClock size={13} />
              <Text size="xs" c="dimmed">
                Queue: {formatDuration(job.delayTimeMs)}
              </Text>
            </Group>
          )}
          {job.executionTimeMs !== null && job.executionTimeMs !== undefined ? (
            <Group gap={4}>
              <IconClock size={13} />
              <Text size="xs" c="dimmed">
                Processing: {formatDuration(job.executionTimeMs)}
              </Text>
            </Group>
          ) : isActive ? (
            <Group gap={4}>
              <IconClock size={13} />
              <Text size="xs" c="dimmed">
                Elapsed: {formatDuration(elapsedMs)}
              </Text>
            </Group>
          ) : null}
        </Group>

        {job.errorMessage && (
          <Text size="xs" c="red">
            {job.errorMessage}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

function JobProgress({ job }: { job: RunPodJob }) {
  const { progressStep, progressTotalSteps, progressCode, progressPercent } = job;
  // The worker always reports step, totalSteps and code together; percent only on sampler steps.
  if (progressStep === null || progressTotalSteps === null || progressCode === null) {
    throw new Error(`Job ${job.id} reports incomplete progress`);
  }

  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">
        Step {progressStep}/{progressTotalSteps} · {progressLabel(progressCode)}
        {progressPercent === null ? "" : ` · ${progressPercent}%`}
      </Text>
      {/* Steps without a sampler percent show a full animated bar as a "working" indicator. */}
      <Progress
        value={progressPercent === null ? 100 : progressPercent}
        size="sm"
        striped
        animated
        aria-label="Generation progress"
      />
    </Stack>
  );
}
